import { db, SyncQueueItem } from './database';
import { isHomeserverConfigured } from '../lib/homeserver';

// Online-first attivo solo con browser online e homeserver same-origin disponibile.
export function isOnlineAndConfigured(): boolean {
  return navigator.onLine && isHomeserverConfigured();
}

export async function getPendingEntityIds(
  entityType: SyncQueueItem['entityType'],
  filter?: (item: SyncQueueItem) => boolean
): Promise<Set<string>> {
  const pending = await db.syncQueue
    .where('entityType')
    .equals(entityType)
    .and((item) => item.synced === 0 && (item.retryCount || 0) < 5)
    .toArray();

  const relevant = filter
    ? pending.filter((item) => item.operation === 'DELETE' || filter(item))
    : pending;
  return new Set(relevant.map((item) => item.entityId));
}

export async function applyPendingWrites<T extends { id: string }>(
  remoteItems: T[],
  entityType: SyncQueueItem['entityType'],
  filter: (item: SyncQueueItem) => boolean
): Promise<T[]> {
  const pending = await db.syncQueue
    .where('entityType')
    .equals(entityType)
    .and((item) => item.synced === 0 && (item.retryCount || 0) < 5)
    .toArray();

  const relevant = pending
    .filter((item) => item.operation === 'DELETE' || filter(item))
    .sort((a, b) => a.timestamp - b.timestamp);
  if (relevant.length === 0) {
    return remoteItems;
  }

  const resultMap = new Map<string, T>(remoteItems.map((item) => [item.id, item]));

  for (const pendingItem of relevant) {
    if (pendingItem.operation === 'DELETE') {
      resultMap.delete(pendingItem.entityId);
      continue;
    }

    resultMap.set(pendingItem.entityId, pendingItem.payload as T);
  }

  return Array.from(resultMap.values());
}

// Dimensione dei blocchi di write-through: una transazione IndexedDB per blocco
// invece di una get+put awaited per riga.
const WRITE_THROUGH_CHUNK_SIZE = 200;

export async function writeThroughCache<T extends { id: string }>(
  remoteItems: T[],
  pendingIds: Set<string>,
  table: any,
  mergeLocalFields?: (remote: T, existing: T | undefined) => T,
  stripForPersistence?: (remote: T) => T
): Promise<T[]> {
  const mergedItems: T[] = [];

  for (let index = 0; index < remoteItems.length; index += WRITE_THROUGH_CHUNK_SIZE) {
    const chunk = remoteItems.slice(index, index + WRITE_THROUGH_CHUNK_SIZE);
    const ids = chunk.map((item) => item.id);

    // Lettura delle righe locali, controllo dei pending e scrittura nella stessa
    // transazione: una modifica locale accodata dopo che il chiamante ha calcolato
    // pendingIds non viene sovrascritta dalla copia remota.
    await db.transaction('rw', table, db.syncQueue, async () => {
      const existingRows: Array<T | undefined> = await table.bulkGet(ids);
      const queued = await db.syncQueue.where('entityId').anyOf(ids).toArray();
      const unsyncedIds = new Set(
        queued.filter((item) => item.synced === 0).map((item) => item.entityId)
      );

      const toPersist: T[] = [];
      chunk.forEach((remoteItem, position) => {
        const existing = existingRows[position];
        const itemToReturn = mergeLocalFields ? mergeLocalFields(remoteItem, existing) : remoteItem;
        mergedItems.push(itemToReturn);

        const hasLocalChanges =
          pendingIds.has(remoteItem.id) ||
          unsyncedIds.has(remoteItem.id) ||
          // Planimetria con file locale non ancora caricato: la riga locale è l'unica copia.
          (existing as any)?.assetDirty === 1;
        if (!hasLocalChanges) {
          toPersist.push(stripForPersistence ? stripForPersistence(itemToReturn) : itemToReturn);
        }
      });

      if (toPersist.length > 0) {
        await table.bulkPut(toPersist);
      }
    });
  }

  return mergedItems;
}

export function isAuthError(err: any): boolean {
  return (
    err?.status === 401 ||
    err?.status === 403 ||
    err?.code === 'PGRST301' ||
    err?.message?.includes('JWT')
  );
}

export function shouldFallbackToIndexedDb(err: any): boolean {
  if (isAuthError(err)) {
    return true;
  }

  const status = typeof err?.status === 'number' ? err.status : undefined;
  if (status && status >= 400 && status < 500) {
    return false;
  }

  // Errori di rete/offline e 5xx già ritentati dal client API possono usare la cache locale.
  return true;
}
